import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import {
  Box,
  Button,
  Grid,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import Header2 from "../../ui-components/headerii";

import SelfServiceServices from "../../services/v1/selfServiceServices";
import DropdownServices from "../../services/v1/dropdownServices";
import DateCalendarServerRequest from "../../ui-components/customCalendar";
import { useSnackbar } from "../../ui-components/snackbarProvider";

// Donor-facing twin of admin/confirmation/OrderEdit.js. Same fields, same
// date-availability check, but authorized by a self-service session token
// (from the OTP flow or an admin-emailed magic link) instead of an admin
// JWT, and it always re-fetches the donor's current details rather than
// trusting anything encoded in the URL.
const SelfServiceEdit = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [token, setToken] = useState(null);
  const [donorId, setDonorId] = useState(null);
  const [sessionInvalid, setSessionInvalid] = useState(false);

  const [data, setData] = useState(null);
  const [reservation, setReservation] = useState(null);
  const [originalReservedDate, setOriginalReservedDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  // Recurring donors (Sponsor Lifetime is 100 pre-generated yearly rows)
  // get asked upfront whether a date change should move just the next
  // occurrence or every remaining one -- null means "still needs asking".
  const [futureOccurrenceCount, setFutureOccurrenceCount] = useState(1);
  const [dateChangeScope, setDateChangeScope] = useState(null);

  const [titleOptions, setTitleOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const d = dayjs(dateString);
    return d.isValid() ? d.format("YYYY-MM-DD") : "";
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryToken = params.get("token");
    const queryDonorId = params.get("donor_id");

    const resolvedToken = queryToken || sessionStorage.getItem("selfServiceToken");
    const resolvedDonorId = queryDonorId || sessionStorage.getItem("selfServiceDonorId");

    if (!resolvedToken || !resolvedDonorId) {
      setSessionInvalid(true);
      setLoading(false);
      return;
    }

    sessionStorage.setItem("selfServiceToken", resolvedToken);
    sessionStorage.setItem("selfServiceDonorId", resolvedDonorId);
    setToken(resolvedToken);
    setDonorId(resolvedDonorId);
  }, [location.search]);

  useEffect(() => {
    if (!token || !donorId) return;

    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const [titles, countries, detailsResponse] = await Promise.all([
          DropdownServices.allTitles(),
          DropdownServices.allCountries(),
          SelfServiceServices.getDonorDetails({ token, donorId }),
        ]);

        setTitleOptions(titles.data || []);
        setCountryOptions(countries.data || []);

        const donorDetails = detailsResponse?.data?.donor_details || {};
        const upcomingReservation = detailsResponse?.data?.reservation || null;

        if (!upcomingReservation) {
          showSnackbar("No upcoming reservation found to edit.", { severity: "warning" });
        }

        const initialDate = formatDate(upcomingReservation?.date);

        setData({
          fullname: donorDetails.fullname || "",
          contactNo: donorDetails.contactNo || "",
          whatsappNo: donorDetails.whatsappNo || "",
          email: donorDetails.email || "",
          profTitle: donorDetails.profTitle || "",
          country: donorDetails.country || "",
          city: donorDetails.city || "",
          message: donorDetails.reasonForSponsoring || "",
          reservedDate: initialDate,
        });
        setReservation(upcomingReservation);
        setOriginalReservedDate(initialDate);

        const recurring = Boolean(detailsResponse?.data?.is_recurring);
        const occurrenceCount = detailsResponse?.data?.future_occurrence_count || 1;
        setFutureOccurrenceCount(occurrenceCount);
        // Only a real choice when there's more than one future occurrence
        // to shift -- otherwise there's nothing to distinguish, skip asking.
        setDateChangeScope(recurring && occurrenceCount > 1 ? null : "single");
      } catch (err) {
        if (err.message && err.message.toLowerCase().includes("session")) {
          setSessionInvalid(true);
        } else {
          showSnackbar("Failed to load your details: " + (err.message || "Server error"), {
            severity: "error",
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, donorId]);

  const handleChange = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsUpdating(true);
    try {
      const donorUpdateResponse = await SelfServiceServices.updateDonorDetails({
        token,
        donorId,
        data: {
          fullname: data.fullname,
          contactNo: data.contactNo,
          whatsappNo: data.whatsappNo,
          email: data.email,
          profTitle: data.profTitle || null,
          country: data.country || null,
          city: data.city,
          reasonForSponsoring: data.message,
        },
      });

      if (donorUpdateResponse.status_code !== 0) {
        throw new Error(donorUpdateResponse.message || "Failed to update your details.");
      }

      if (reservation && data.reservedDate !== originalReservedDate) {
        const dateUpdateResponse =
          dateChangeScope === "series"
            ? await SelfServiceServices.updateReservationDateSeries({
                token,
                donorId,
                newDate: data.reservedDate,
                oldDate: originalReservedDate,
              })
            : await SelfServiceServices.updateReservationDate({
                token,
                donorId,
                reservationId: reservation.reservation_id,
                newDate: data.reservedDate,
                oldDate: originalReservedDate,
                fullname: data.fullname,
                contactNo: data.contactNo,
                whatsappNo: data.whatsappNo,
              });

        if (dateUpdateResponse.status_code !== 0) {
          throw new Error(dateUpdateResponse.message || "Failed to update the reservation date.");
        }
        setOriginalReservedDate(data.reservedDate);
      }

      showSnackbar("Your details have been updated. Thank you!", { severity: "success" });
      setTimeout(() => navigate("/"), 1800);
      return;
    } catch (err) {
      if (err.message && err.message.toLowerCase().includes("session")) {
        setSessionInvalid(true);
      }
      showSnackbar(err.message || "Something went wrong. Please try again.", { severity: "error" });
    } finally {
      setIsUpdating(false);
    }
  };

  if (sessionInvalid) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
        <Header2 />
        <Typography variant="h6" sx={{ mt: 5 }}>
          Your session has expired or this link is no longer valid.
        </Typography>
        <Button variant="contained" sx={{ mt: 2, bgcolor: "#8B2757" }} onClick={() => navigate("/")}>
          Start over from the homepage
        </Button>
      </Box>
    );
  }

  if (loading || !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress color="secondary" />
        <Typography sx={{ ml: 2 }}>Loading your details...</Typography>
      </Box>
    );
  }

  if (dateChangeScope === null) {
    return (
      <Box>
        <Header2 />
        <Grid container justifyContent="center" sx={{ mt: 5 }}>
          <Grid item xs={10} sm={8} md={6}>
            <Typography variant="h5" sx={{ mb: 2, textAlign: "center" }}>
              This is a recurring sponsorship
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3, textAlign: "center" }}>
              You have {futureOccurrenceCount} upcoming dates on this sponsorship.
              Would you like to change just the next one, or move all of them?
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Button
                variant="contained"
                sx={{ bgcolor: "#8B2757" }}
                onClick={() => setDateChangeScope("single")}
              >
                Just the next date
              </Button>
              <Button
                variant="outlined"
                sx={{ borderColor: "#8B2757", color: "#8B2757" }}
                onClick={() => setDateChangeScope("series")}
              >
                All {futureOccurrenceCount} future dates
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <Header2 />
      <Typography variant="h5" sx={{ mt: 3, fontWeight: "semibold", fontVariantCaps: "all-petite-caps", textAlign: "center" }}>
        Update Your Sponsorship Details
      </Typography>
      <Grid container justifyContent="center" sx={{ mt: 3 }}>
        <Grid item xs={10} sm={8} md={6}>
          <Box sx={{ p: 3, border: "1px solid lightgrey", borderRadius: "8px", boxShadow: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={titleOptions}
                  getOptionLabel={(option) => option.title || ""}
                  value={titleOptions.find((o) => o.title === data.profTitle) || null}
                  onChange={(e, newValue) => handleChange("profTitle", newValue ? newValue.title : "")}
                  renderInput={(params) => <TextField {...params} label="Title" fullWidth margin="normal" />}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  label="Full Name"
                  value={data.fullname}
                  onChange={(e) => handleChange("fullname", e.target.value)}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Contact No"
                  value={data.contactNo}
                  onChange={(e) => handleChange("contactNo", e.target.value)}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="WhatsApp No"
                  value={data.whatsappNo}
                  onChange={(e) => handleChange("whatsappNo", e.target.value)}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="email"
                  value={data.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="City"
                  value={data.city}
                  onChange={(e) => handleChange("city", e.target.value)}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={countryOptions}
                  getOptionLabel={(option) => option.country || ""}
                  value={countryOptions.find((o) => o.country === data.country) || null}
                  onChange={(e, newValue) => handleChange("country", newValue ? newValue.country : "")}
                  renderInput={(params) => <TextField {...params} label="Country" fullWidth margin="normal" />}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Reason for Sponsoring"
                  value={data.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  margin="normal"
                />
              </Grid>
            </Grid>

            {reservation && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Reserved Date
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Currently reserved for {originalReservedDate}. Pick a
                  different date below if you'd like to move it -- dates
                  already taken by other donors are greyed out.
                  {dateChangeScope === "series" && (
                    <>
                      {" "}Since you chose to move all future dates, every
                      remaining occurrence will shift by the same amount.
                    </>
                  )}
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <DateCalendarServerRequest
                    value={data.reservedDate}
                    excludeDate={originalReservedDate}
                    onDateChange={(date) =>
                      handleChange("reservedDate", dayjs(date).format("YYYY-MM-DD"))
                    }
                  />
                </Box>
              </Box>
            )}

            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button variant="outlined" color="secondary" onClick={() => navigate("/")}>
                Go Back
              </Button>
              <Button
                variant="contained"
                sx={{ bgcolor: "#8B2757" }}
                onClick={handleSave}
                disabled={isUpdating}
              >
                {isUpdating ? <CircularProgress size={24} /> : "Save Changes"}
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SelfServiceEdit;
